import { useCallback, useSyncExternalStore } from 'react'

/**
 * A UI preference kept in localStorage and shared across every consumer and
 * browser tab. Extracted so each new preference doesn't repeat the
 * useSyncExternalStore + storage-event wiring.
 */
export function createPersistedPreference<T>({
  key,
  fallback,
  parse,
}: {
  key: string
  fallback: T | (() => T)
  /** Returns undefined when the stored string isn't a valid value. */
  parse: (raw: string) => T | undefined
}) {
  const event = `titan:pref:${key}`

  const resolveFallback = (): T =>
    typeof fallback === 'function' ? (fallback as () => T)() : fallback

  const read = (): T => {
    try {
      const raw = window.localStorage.getItem(key)
      if (raw == null) return resolveFallback()
      return parse(raw) ?? resolveFallback()
    } catch {
      return resolveFallback()
    }
  }

  const subscribe = (onChange: () => void) => {
    window.addEventListener(event, onChange)
    window.addEventListener('storage', onChange)
    return () => {
      window.removeEventListener(event, onChange)
      window.removeEventListener('storage', onChange)
    }
  }

  const set = (value: T) => {
    try {
      window.localStorage.setItem(key, String(value))
    } catch {
      // Preference is non-critical; still notify so the UI updates.
    }
    window.dispatchEvent(new Event(event))
  }

  const usePreference = (): [T, (value: T) => void] => {
    const value = useSyncExternalStore(subscribe, read, resolveFallback)
    const setValue = useCallback((next: T) => set(next), [])
    return [value, setValue]
  }

  return { usePreference, set, read }
}
